import { Card, Col, Row } from 'react-bootstrap';
import OrderCopperDonutChart from '../order-management-charts/OrderCopperDonutChart';
import OrderPlasticBarChart from '../order-management-charts/OrderPlasticBarChart';

const upper = v =>
  String(v || '')
    .trim()
    .toUpperCase();
const safeArr = v => (Array.isArray(v) ? v : []);

const round3 = n => Number(Number(n || 0).toFixed(3));

const getOrderMaterialSummary = order => {
  if (safeArr(order?.materialPlanSummary).length) {
    return order.materialPlanSummary;
  }

  const map = new Map();

  const keyOf = x =>
    [
      upper(x?.material),
      upper(x?.form),
      upper(x?.wireType || ''),
      x?.wireDiameter != null ? String(Number(x.wireDiameter)) : '',
      upper(x?.grade || ''),
      upper(x?.color || ''),
      upper(x?.layer || '')
    ].join('|');

  for (const line of safeArr(order?.lines)) {
    // A cancelled line needs no material. Summing it in was how a cancelled
    // order went on asking for its copper — and on older orders, whose stored
    // summaries predate the fix, this is still the only thing that catches it.
    if (String(line?.status || '').toUpperCase() === 'CANCELLED') continue;
    for (const row of safeArr(line?.materialPlanSummary)) {
      const key = keyOf(row);
      if (!map.has(key)) {
        map.set(key, { ...row, planned_kg: 0 });
      }
      map.get(key).planned_kg += Number(row?.planned_kg) || 0;
    }
  }

  return Array.from(map.values()).map(item => ({
    ...item,
    planned_kg: round3(item.planned_kg)
  }));
};

const aggregateCopper = summary => {
  const map = new Map();

  for (const row of safeArr(summary)) {
    if (upper(row?.material) !== 'COPPER') continue;

    const wireType = upper(row?.wireType || 'COPPER');
    const wireDiameter = Number(row?.wireDiameter) || 0;
    const key = `${wireType}|${wireDiameter}`;

    if (!map.has(key)) {
      map.set(key, {
        wireType,
        wireDiameter,
        planned_kg: 0
      });
    }

    map.get(key).planned_kg += Number(row?.planned_kg) || 0;
  }

  return Array.from(map.values())
    .map(item => ({
      ...item,
      planned_kg: round3(item.planned_kg)
    }))
    .sort((a, b) => (a.wireDiameter || 0) - (b.wireDiameter || 0));
};

const aggregatePlastics = summary => {
  const map = new Map();

  for (const row of safeArr(summary)) {
    const material = upper(row?.material);
    if (material !== 'PVC' && material !== 'POLIETILEN') continue;

    const grade = upper(row?.grade || 'NO-GRADE');
    const key = `${material}|${grade}`;

    if (!map.has(key)) {
      map.set(key, {
        material,
        grade,
        planned_kg: 0
      });
    }

    map.get(key).planned_kg += Number(row?.planned_kg) || 0;
  }

  return Array.from(map.values())
    .map(item => ({
      ...item,
      planned_kg: round3(item.planned_kg)
    }))
    .sort((a, b) => {
      const m = upper(a.material).localeCompare(upper(b.material));
      if (m !== 0) return m;
      return upper(a.grade).localeCompare(upper(b.grade));
    });
};

// BLR (steel armour tape) grouped by wide × thickness
const aggregateBLR = summary => {
  const map = new Map();
  for (const row of safeArr(summary)) {
    const material = upper(row?.material);
    if (material !== 'BLR' && material !== 'STEEL') continue;
    const spec = row?.grade || (row?.wide && row?.thickness ? `${row.wide} x ${row.thickness}` : 'armouring');
    if (!map.has(spec)) map.set(spec, { spec, planned_kg: 0 });
    map.get(spec).planned_kg += Number(row?.planned_kg) || 0;
  }
  return Array.from(map.values())
    .map(i => ({ ...i, planned_kg: round3(i.planned_kg) }))
    .sort((a, b) => b.planned_kg - a.planned_kg);
};

const OrderMaterialsOverview = ({ order }) => {
  const summary = getOrderMaterialSummary(order);
  const copperData = aggregateCopper(summary);
  const plasticData = aggregatePlastics(summary);
  const blrData = aggregateBLR(summary);

  const totalCopperKg = copperData.reduce(
    (sum, item) => sum + (Number(item?.planned_kg) || 0),
    0
  );
  const totalBlrKg = round3(
    blrData.reduce((sum, item) => sum + (Number(item?.planned_kg) || 0), 0)
  );

  return (
    <Card className="h-100">
      <Card.Body>
        <Row className="g-1 align-items-stretch">
          <Col xs={12} xl={6}>
            <h4 className="text-1100 mb-0">Copper Required</h4>
            <OrderCopperDonutChart data={copperData} totalKg={totalCopperKg} />
          </Col>

          <Col xs={12} xl={6}>
            <h4 className="text-1100 mb-4">Plastics Required</h4>
            <OrderPlasticBarChart data={plasticData} />
          </Col>
        </Row>

        {blrData.length > 0 && (
          <Row className="mt-3">
            <Col xs={12}>
              <h4 className="text-1100 mb-2">BLR Required — {totalBlrKg} kg</h4>
              <div className="d-flex gap-2 flex-wrap">
                {blrData.map(b => (
                  <span key={b.spec} className="badge bg-secondary">
                    BLR {b.spec}: {b.planned_kg} kg
                  </span>
                ))}
              </div>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

export default OrderMaterialsOverview;
